import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Flex } from "@mantine/core";
import { IconCopy } from "@tabler/icons-react";
import { Row, RowSelectionState } from "@tanstack/react-table";
import dayjs from "dayjs";
import { CampaignInquiryQueryDataType } from "src/hooks/campaignInquiries";
import { RoutePath } from "src/routes";
import { RoutePathParams } from "src/utils/buildUrl";
import { checkCountryCode } from "src/utils/helpers";

import { createColumnHelper, DataGrid } from "../DataGrid";
import { ActionLink } from "../DataGrid/TableRow/TableRow";

const columnHelper = createColumnHelper<CampaignInquiryQueryDataType>();

export interface CampaignInquiriesDataGridProps {
    campaignInquiries: CampaignInquiryQueryDataType[];
    isLoadingCampaignInquiries: boolean;
    onDuplicate?: (row: CampaignInquiryQueryDataType) => void;
    getRowHref?: (row: Row<CampaignInquiryQueryDataType>) => RoutePathParams<RoutePath>;
    onSelectedCampaignInquiriesChange?(campaignInquiries: CampaignInquiryQueryDataType[]);
}

export const CampaignInquiriesDataGrid = ({
    campaignInquiries,
    isLoadingCampaignInquiries,
    getRowHref,
    onSelectedCampaignInquiriesChange,
}: CampaignInquiriesDataGridProps) => {
    const { t } = useTranslation(["common"]);

    const enableSelection = typeof onSelectedCampaignInquiriesChange === "function";
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const columns = useMemo(() => {
        const columns = [
            columnHelper.accessor("name", {
                id: "name",
                header: t("campaigninquiry_name"),
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("createdAt", {
                id: "createdat",
                header: t("campaigninquiry_createdat"),
                footer: (props) => props.column.id,
                cell: (props) => (props?.getValue?.() ? dayjs(props.getValue()!).format("DD.MM.YYYY HH:mm") : "-"),
            }),
            columnHelper.accessor("tenant", {
                id: "tenant",
                header: t("campaigninquiry_tenant"),
                footer: (props) => props.column.id,
                cell: (props) => {
                    const country = props?.getValue?.()?.id;

                    if (!country || !checkCountryCode(country)) {
                        return "-";
                    }

                    return t(`country_${country}`);
                },
            }),
        ];

        if (enableSelection) {
            columns.unshift(
                columnHelper.display({
                    id: "_rowselection",
                    footer: (props) => props.column.id,
                    header: ({ table }) => (
                        <Checkbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                    ),
                    cell: ({ row }) => (
                        <Flex display="inline-flex" align="center">
                            <Checkbox
                                mr="xs"
                                checked={row.getIsSelected()}
                                disabled={!row.getCanSelect()}
                                indeterminate={row.getIsSomeSelected()}
                                onChange={row.getToggleSelectedHandler()}
                            />
                        </Flex>
                    ),
                })
            );
        }

        return columns;
    }, [t, enableSelection]);

    const actions: ActionLink<CampaignInquiryQueryDataType>[] = useMemo(
        () => [
            {
                type: "link",
                icon: <IconCopy size="1.15rem" />,
                label: t("copy"),
                getRoutePathParams: (row) => {
                    const routePathParams: RoutePathParams<RoutePath> = {
                        path: RoutePath.CAMPAIGNS_INQUIRY_CREATE_DUPLICATE_FROM_INQUIRY,
                        params: {
                            campaignInquiryId: row.id,
                        },
                    };

                    return routePathParams;
                },
            },
        ],
        [t]
    );

    const campaignsUpdateKey = campaignInquiries.map((campaignInquiry) => `${campaignInquiry?.id}`).join("");

    useEffect(() => {
        const selectedCampaigns = campaignInquiries.filter((campaignInquiry) => {
            return rowSelection?.[campaignInquiry?.id];
        });

        onSelectedCampaignInquiriesChange?.(selectedCampaigns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSelection, campaignsUpdateKey, JSON.stringify(rowSelection), onSelectedCampaignInquiriesChange]);

    return (
        <DataGrid
            isLoading={isLoadingCampaignInquiries}
            columns={columns}
            data={campaignInquiries}
            getRowHref={getRowHref}
            getRowId={(campaign) => campaign?.id}
            mobileHeaderIndex={0}
            secMobileHeaderIndex={2}
            enableRowSelection={enableSelection}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            actions={actions}
        />
    );
};
